import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./SingleLoanEmiPayment.css";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

const API_URL = "https://payyug.in/api";

const formatDateDisplay = (dateValue) => {
  if (!dateValue) return "N/A";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleDateString("en-GB");
};

const toInputDate = (dateValue = new Date()) => {
  const d = new Date(dateValue);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const SingleLoanEmiPayment = () => {
  const superAdminId = localStorage.getItem("adminuserId") || "";
  const navigate=useNavigate()

  const [branches, setBranches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedCenterId, setSelectedCenterId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [emis, setEmis] = useState([]);
  const [lastReceiptText, setLastReceiptText] = useState("No Record");

  const [paymentMode, setPaymentMode] = useState("Cash");
  const [collectionDate, setCollectionDate] = useState(toInputDate());
  const [remarks, setRemarks] = useState("");

  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await axios.get(`${API_URL}/get-branch`, {
          params: {
            SuperAdminId: superAdminId,
            limit: "all",
          },
        });
        setBranches(res?.data?.branches || []);
      } catch (err) {
        console.error("Failed to load branches", err);
      }
    };

    if (superAdminId) {
      fetchBranches();
    }
  }, [superAdminId]);

  useEffect(() => {
    if (!selectedBranchId) {
      setCenters([]);
      setSelectedCenterId("");
      setGroups([]);
      setSelectedGroupId("");
      setEmployees([]);
      setSelectedEmployeeId("");
      setSelectedEmployee(null);
      setEmis([]);
      setLastReceiptText("No Record");
      return;
    }

    const fetchCenters = async () => {
      try {
        const res = await axios.get(`${API_URL}/group/centre`, {
          params: {
            superAdminId,
            branchid: selectedBranchId,
            limit: 100000,
          },
        });
        setCenters(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to load centers", err);
        setCenters([]);
      }
    };

    fetchCenters();
    setSelectedCenterId("");
    setGroups([]);
    setSelectedGroupId("");
    setEmployees([]);
    setSelectedEmployeeId("");
    setSelectedEmployee(null);
    setEmis([]);
    setLastReceiptText("No Record");
  }, [selectedBranchId, superAdminId]);

  useEffect(() => {
    if (!selectedCenterId) {
      setGroups([]);
      setSelectedGroupId("");
      setEmployees([]);
      setSelectedEmployeeId("");
      setSelectedEmployee(null);
      setEmis([]);
      setLastReceiptText("No Record");
      return;
    }

    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API_URL}/groupupdate/getall`, {
          params: {
            superAdminId,
            CenterId: selectedCenterId,
            limit: 100000,
          },
        });
        setGroups(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to load groups", err);
        setGroups([]);
      }
    };

    fetchGroups();
    setSelectedGroupId("");
    setEmployees([]);
    setSelectedEmployeeId("");
    setSelectedEmployee(null);
    setEmis([]);
    setLastReceiptText("No Record");
  }, [selectedCenterId, superAdminId]);

  useEffect(() => {
    if (!selectedBranchId || !selectedCenterId) {
      setEmployees([]);
      setSelectedEmployeeId("");
      setSelectedEmployee(null);
      setEmis([]);
      setLastReceiptText("No Record");
      return;
    }

    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/group-loan/group/list`, {
          params: {
            superAdminId,
            branchId: selectedBranchId,
            centerId: selectedCenterId,
            groupId: selectedGroupId === "all" ? "" : selectedGroupId,
            page: 1,
            limit: 1000,
          },
        });
        setEmployees(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to load employees", err);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
    setSelectedEmployeeId("");
    setSelectedEmployee(null);
    setEmis([]);
    setLastReceiptText("No Record");
  }, [selectedBranchId, selectedCenterId, selectedGroupId, superAdminId]);

  useEffect(() => {
    if (!selectedEmployeeId) {
      setSelectedEmployee(null);
      setEmis([]);
      setLastReceiptText("No Record");
      return;
    }

    const employeeLoan =
      employees.find((emp) => String(emp.id) === String(selectedEmployeeId)) ||
      null;
    setSelectedEmployee(employeeLoan);

    const fetchEmis = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/group-loan/emi/list`, {
          params: {
            superAdminId,
            grouploanAccountId: selectedEmployeeId,
            page: 1,
            limit: 200,
          },
        });

        const emiList = res?.data?.data || [];
        setEmis(emiList);

        const paidEmis = emiList.filter(
          (emi) => String(emi?.status || "").toLowerCase() === "paid",
        );
        if (paidEmis.length > 0) {
          const latestPaid = paidEmis.reduce((acc, curr) => {
            const accTime = new Date(acc.lastPaidDate || acc.updatedAt || 0).getTime();
            const currTime = new Date(curr.lastPaidDate || curr.updatedAt || 0).getTime();
            return currTime > accTime ? curr : acc;
          }, paidEmis[0]);
          setLastReceiptText(`EMI #${latestPaid.emiNumber || latestPaid.id} Paid`);
        } else {
          setLastReceiptText("No Record");
        }
      } catch (err) {
        console.error("Failed to load EMI details", err);
        setEmis([]);
        setLastReceiptText("No Record");
      } finally {
        setLoading(false);
      }
    };

    fetchEmis();
  }, [selectedEmployeeId, employees, superAdminId]);

  const paidCount = useMemo(
    () => emis.filter((emi) => String(emi?.status || "").toLowerCase() === "paid").length,
    [emis],
  );

  const totalInstallments = emis.length;

  const upcomingUnpaidEmi = useMemo(() => {
    const pending = emis.filter((emi) => String(emi?.status || "").toLowerCase() !== "paid");
    if (pending.length === 0) return null;

    return [...pending].sort((a, b) => {
      const aTime = new Date(a.dueDate || 0).getTime();
      const bTime = new Date(b.dueDate || 0).getTime();
      return aTime - bTime;
    })[0];
  }, [emis]);

  const baseEmiAmount = Number(upcomingUnpaidEmi?.emiAmount || 0);

  const overdueDays = useMemo(() => {
    if (!upcomingUnpaidEmi?.dueDate) return 0;
    const today = new Date();
    const dueDate = new Date(upcomingUnpaidEmi.dueDate);
    const diff = Math.floor((today.setHours(0, 0, 0, 0) - dueDate.setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  }, [upcomingUnpaidEmi]);

  const totalPenalty = Number(
    upcomingUnpaidEmi?.penaltyAmount ||
      upcomingUnpaidEmi?.calculatedPenalty ||
      upcomingUnpaidEmi?.penalty ||
      0,
  );

  const penaltyPerDay = overdueDays > 0 ? totalPenalty / overdueDays : 0;
  const totalCollectionAmount = baseEmiAmount + totalPenalty;

  const progressPercent = totalInstallments > 0 ? (paidCount / totalInstallments) * 100 : 0;

  const handlePayAndGenerateReceipt = async () => {
    if (!upcomingUnpaidEmi?.id) {
      alert("No due EMI found for this account.");
      return;
    }

    try {
      setPaying(true);

      await axios.post(`${API_URL}/group-loan/emi/pay`, {
        emiId: upcomingUnpaidEmi.id,
        paidAmount: totalCollectionAmount,
        paymentMode,
        paidDate: collectionDate,
        remark: remarks || "Single EMI payment",
      });

      alert("Payment processed successfully.");

      const refresh = await axios.get(`${API_URL}/group-loan/emi/list`, {
        params: {
          superAdminId,
          grouploanAccountId: selectedEmployeeId,
          page: 1,
          limit: 200,
        },
      });
      const emiList = refresh?.data?.data || [];
      setEmis(emiList);
      setRemarks("");
    } catch (err) {
      console.error("Payment failed", err);
      alert(err?.response?.data?.message || "Payment failed.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="single-emi-payment-page">
      {/* Back wrapper */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <ArrowLeftIcon
          onClick={() => navigate(-1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />

        <h2
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          Group Loan Single Emi Payment
        </h2>

        <ArrowRightIcon
          onClick={() => navigate(1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />
      </div>
      <div className="single-emi-payment-filters">
        <div className="single-emi-filter-group">
          <label>Select Branch</label>
          <select
            value={selectedBranchId}
            onChange={(e) => setSelectedBranchId(e.target.value)}
          >
            <option value="">Select Branch</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>

        <div className="single-emi-filter-group">
          <label>Select Center</label>
          <select
            value={selectedCenterId}
            onChange={(e) => setSelectedCenterId(e.target.value)}
            disabled={!selectedBranchId}
          >
            <option value="">Select Center</option>
            {centers.map((center) => (
              <option key={center.id} value={center.id}>
                {center.CenterName}
              </option>
            ))}
          </select>
        </div>

        <div className="single-emi-filter-group">
          <label>Select Group</label>
          <select
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            disabled={!selectedCenterId}
          >
            <option value="">Select Group</option>
            <option value="all">All</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.groupname}
              </option>
            ))}
          </select>
        </div>

        <div className="single-emi-filter-group">
          <label>Select Employee</label>
          <select
            value={selectedEmployeeId}
            onChange={(e) => setSelectedEmployeeId(e.target.value)}
            disabled={!selectedGroupId || loading}
          >
            <option value="">Select Employee Loan Account</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {(emp.memberName || "Unknown")} | {(emp.loanAccountNumber || "N/A")}
              </option>
            ))}
          </select>
        </div>
      </div>

      <section className="single-emi-payment-card">
        <h2 className="single-emi-title">
          <i className="fa-solid fa-calendar-check"></i> EMI Schedule Summary
        </h2>

        {selectedEmployee && (
          <div className="single-emi-loan-details">
            <div>
              <span>Loan Details</span>
              <strong>{selectedEmployee.memberName || "N/A"}</strong>
            </div>
            <div>
              <span>Frequency</span>
              <strong>{selectedEmployee.loanRecoveryFrequency || "N/A"}</strong>
            </div>
            <div>
              <span>Status</span>
              <strong>{(selectedEmployee.status || "N/A").toUpperCase()}</strong>
            </div>
          </div>
        )}

        <div className="single-emi-progress-row">
          <label>Payment Progress</label>
          <div className="single-emi-progress-wrap">
            <div
              className="single-emi-progress-fill"
              style={{ width: `${progressPercent}%` }}
            ></div>
            <span>{paidCount} / {totalInstallments} Installments Paid</span>
          </div>
        </div>

        <div className="single-emi-grid two-col">
          <div className="single-emi-field">
            <label>Loan Account Search</label>
            <div className="single-emi-box">{selectedEmployee?.loanAccountNumber || "Search Account..."}</div>
          </div>

          <div className="single-emi-field">
            <label>Last Receipt</label>
            <div className="single-emi-box">{lastReceiptText}</div>
          </div>

          <div className="single-emi-field">
            <label>Upcoming Due Date</label>
            <div className={`single-emi-box ${overdueDays > 0 ? "warn" : ""}`}>
              {formatDateDisplay(upcomingUnpaidEmi?.dueDate)}
            </div>
          </div>

          <div className="single-emi-field">
            <label>Base EMI Amount</label>
            <div className="single-emi-box">Rs {baseEmiAmount.toFixed(2)}</div>
          </div>
        </div>

        {overdueDays > 0 && (
          <div className="single-emi-overdue-box">
            <h4>
              <i className="fa-solid fa-triangle-exclamation"></i> Late Payment Overdue Details
            </h4>
            <div className="single-emi-overdue-grid">
              <div>
                <span>Overdue Days</span>
                <strong>{overdueDays} Days</strong>
              </div>
              <div>
                <span>Penalty/Day</span>
                <strong>Rs {penaltyPerDay.toFixed(2)}</strong>
              </div>
              <div>
                <span>Total Penalty</span>
                <strong>Rs {totalPenalty.toFixed(2)}</strong>
              </div>
            </div>
          </div>
        )}

        <div className="single-emi-grid two-col">
          <div className="single-emi-field">
            <label>Calculated Penalty (INR)</label>
            <div className={`single-emi-box ${totalPenalty > 0 ? "danger" : ""}`}>
              Rs {totalPenalty.toFixed(2)}
            </div>
          </div>

          <div className="single-emi-field">
            <label>Total Collection Amount</label>
            <div className="single-emi-box success">Rs {totalCollectionAmount.toFixed(2)}</div>
          </div>

          <div className="single-emi-field">
            <label>Payment Mode</label>
            <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Bank Transfer">Bank Transfer</option>
            </select>
          </div>

          <div className="single-emi-field">
            <label>Collection Date</label>
            <input
              type="date"
              value={collectionDate}
              onChange={(e) => setCollectionDate(e.target.value)}
            />
          </div>
        </div>

        <div className="single-emi-field full-width">
          <label>Remarks</label>
          <input
            type="text"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Optional narration"
          />
        </div>

        <button
          type="button"
          className="single-emi-pay-btn"
          onClick={handlePayAndGenerateReceipt}
          disabled={!selectedEmployeeId || !upcomingUnpaidEmi || paying}
        >
          {paying ? "Processing..." : "Process Payment & Generate Receipt"}
        </button>
      </section>
    </div>
  );
};

export default SingleLoanEmiPayment;
